/**
 * Industry-Standard Notification System
 * Provides toast notifications, alerts, and error displays
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle, 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  X,
  RefreshCcw,
  ExternalLink
} from 'lucide-react';

export enum NotificationType {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info'
}

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number; // Auto-dismiss time in ms, null for persistent
  actions?: NotificationAction[];
  metadata?: Record<string, any>;
  timestamp: Date;
}

export interface NotificationAction {
  label: string;
  action: () => void;
  variant?: 'default' | 'outline' | 'destructive';
  icon?: React.ComponentType<{ className?: string }>;
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => string;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  showSuccess: (title: string, message?: string, options?: Partial<Notification>) => string;
  showError: (title: string, message?: string, options?: Partial<Notification>) => string;
  showWarning: (title: string, message?: string, options?: Partial<Notification>) => string;
  showInfo: (title: string, message?: string, options?: Partial<Notification>) => string;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
  maxNotifications?: number;
  defaultDuration?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  maxNotifications = 5,
  defaultDuration = 5000,
  position = 'top-right'
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const generateId = () => `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const id = generateId();
    const newNotification: Notification = {
      ...notification,
      id,
      timestamp: new Date(),
      duration: notification.duration !== undefined ? notification.duration : defaultDuration
    };

    setNotifications(prev => {
      const updated = [newNotification, ...prev].slice(0, maxNotifications);
      return updated;
    });

    // Auto-remove if duration is set
    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  }, [maxNotifications, defaultDuration]);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const showSuccess = useCallback((title: string, message?: string, options?: Partial<Notification>) => {
    return addNotification({
      type: NotificationType.SUCCESS,
      title,
      message,
      ...options
    });
  }, [addNotification]);

  const showError = useCallback((title: string, message?: string, options?: Partial<Notification>) => {
    return addNotification({
      type: NotificationType.ERROR,
      title,
      message,
      duration: undefined, // Errors are persistent by default
      ...options
    });
  }, [addNotification]);

  const showWarning = useCallback((title: string, message?: string, options?: Partial<Notification>) => {
    return addNotification({
      type: NotificationType.WARNING,
      title,
      message,
      ...options
    });
  }, [addNotification]);

  const showInfo = useCallback((title: string, message?: string, options?: Partial<Notification>) => {
    return addNotification({
      type: NotificationType.INFO,
      title,
      message,
      ...options
    });
  }, [addNotification]);

  const contextValue: NotificationContextType = {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <NotificationContainer notifications={notifications} position={position} onRemove={removeNotification} />
    </NotificationContext.Provider>
  );
};

interface NotificationContainerProps {
  notifications: Notification[];
  position: string;
  onRemove: (id: string) => void;
}

const NotificationContainer: React.FC<NotificationContainerProps> = ({
  notifications,
  position,
  onRemove
}) => {
  const getPositionClasses = () => {
    const baseClasses = 'fixed z-50 flex flex-col gap-2 p-4 pointer-events-none';
    
    switch (position) {
      case 'top-right':
        return `${baseClasses} top-0 right-0`;
      case 'top-left':
        return `${baseClasses} top-0 left-0`;
      case 'bottom-right':
        return `${baseClasses} bottom-0 right-0`;
      case 'bottom-left':
        return `${baseClasses} bottom-0 left-0`;
      case 'top-center':
        return `${baseClasses} top-0 left-1/2 transform -translate-x-1/2`;
      case 'bottom-center':
        return `${baseClasses} bottom-0 left-1/2 transform -translate-x-1/2`;
      default:
        return `${baseClasses} top-0 right-0`;
    }
  };

  if (notifications.length === 0) return null;

  return (
    <div className={getPositionClasses()}>
      {notifications.map((notification) => (
        <NotificationCard
          key={notification.id}
          notification={notification}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
};

interface NotificationCardProps {
  notification: Notification;
  onRemove: (id: string) => void;
}

const NotificationCard: React.FC<NotificationCardProps> = ({ notification, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    // Trigger animation
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  const handleRemove = () => {
    setIsRemoving(true);
    setTimeout(() => onRemove(notification.id), 300);
  };

  const getIcon = () => {
    const iconClasses = "h-5 w-5 flex-shrink-0";
    
    switch (notification.type) {
      case NotificationType.SUCCESS:
        return <CheckCircle className={`${iconClasses} text-status-success`} />;
      case NotificationType.ERROR:
        return <AlertCircle className={`${iconClasses} text-status-error`} />;
      case NotificationType.WARNING:
        return <AlertTriangle className={`${iconClasses} text-status-warning`} />;
      case NotificationType.INFO:
        return <Info className={`${iconClasses} text-status-info`} />;
      default:
        return <Info className={`${iconClasses} text-status-idle`} />;
    }
  };

  const getBorderColor = () => {
    switch (notification.type) {
      case NotificationType.SUCCESS:
        return 'border-l-green-500';
      case NotificationType.ERROR:
        return 'border-l-red-500';
      case NotificationType.WARNING:
        return 'border-l-yellow-500';
      case NotificationType.INFO:
        return 'border-l-blue-500';
      default:
        return 'border-l-gray-500';
    }
  };

  return (
    <Card
      className={`
        min-w-[320px] max-w-[480px] pointer-events-auto border-l-4 ${getBorderColor()}
        transition-all duration-300 ease-in-out transform
        ${isVisible && !isRemoving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        ${isRemoving ? 'scale-95' : 'scale-100'}
        shadow-lg hover:shadow-xl
      `}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {getIcon()}
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h4 className="font-semibold text-sm text-foreground truncate">
                {notification.title}
              </h4>
              
              <div className="flex items-center gap-1">
                {notification.type === NotificationType.ERROR && (
                  <Badge variant="destructive" className="text-xs">
                    Error
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemove}
                  className="h-6 w-6 p-0 hover:bg-muted"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
            
            {notification.message && (
              <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                {typeof notification.message === 'string' ? notification.message : 
                 typeof notification.message === 'object' ? JSON.stringify(notification.message) : 
                 String(notification.message)}
              </p>
            )}
            
            {notification.actions && notification.actions.length > 0 && (
              <div className="flex gap-2 mt-3">
                {notification.actions.map((action, index) => {
                  const Icon = action.icon;
                  return (
                    <Button
                      key={index}
                      variant={action.variant || 'outline'}
                      size="sm"
                      onClick={() => {
                        action.action();
                        handleRemove();
                      }}
                      className="text-xs"
                    >
                      {Icon && <Icon className="h-3 w-3 mr-1" />}
                      {action.label}
                    </Button>
                  );
                })}
              </div>
            )}
            
            {notification.duration === null && (
              <div className="flex items-center justify-between mt-2 pt-2 border-t">
                <span className="text-xs text-muted-foreground">
                  {notification.timestamp.toLocaleTimeString()}
                </span>
                {notification.type === NotificationType.ERROR && (
                  <span className="text-xs text-muted-foreground">
                    Click X to dismiss
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Hook for common error handling patterns
export const useErrorNotifications = () => {
  const { showError, showWarning } = useNotifications();

  const showNetworkError = useCallback((retry?: () => void) => {
    return showError(
      'Connection Problem',
      'Unable to connect to the server. Please check your internet connection.',
      {
        actions: retry ? [
          {
            label: 'Retry',
            action: retry,
            icon: RefreshCcw,
            variant: 'default'
          }
        ] : undefined
      }
    );
  }, [showError]);

  const showAuthError = useCallback(() => {
    return showError(
      'Authentication Required',
      'Your session has expired. Please log in again.',
      {
        actions: [
          {
            label: 'Login',
            action: () => window.location.href = '/login',
            variant: 'default'
          }
        ]
      }
    );
  }, [showError]);

  const showPermissionError = useCallback((resource: string) => {
    return showError(
      'Access Denied',
      `You don't have permission to access ${resource}.`,
      {
        actions: [
          {
            label: 'Contact Support',
            action: () => window.open('mailto:support@chatslytics.com'),
            icon: ExternalLink,
            variant: 'outline'
          }
        ]
      }
    );
  }, [showError]);

  const showValidationError = useCallback((field: string, message: string) => {
    return showWarning(
      `Invalid ${field}`,
      message,
      {
        duration: 4000
      }
    );
  }, [showWarning]);

  return {
    showNetworkError,
    showAuthError,
    showPermissionError,
    showValidationError
  };
};

export default NotificationProvider;